import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ComparisonTable from './components/ComparisonTable';
import ComparisonCards from './components/ComparisonCards';
import ComparisonCTA from './components/ComparisonCTA';
import DiagnosisBanner from './components/DiagnosisBanner';
import { productData } from '../../mocks/products';

export default function ComparePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-white shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="cursor-pointer z-50">
            <img 
              src={scrolled 
                ? "https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/826c552476badc913a23e7605116bff7.png"
                : "https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/5bc5af811e2f931fbde5d199712dd10f.png"
              }
              alt="MeVER Chair ロゴ" 
              title="MeVER Chair"
              className="h-7 sm:h-8 w-auto"
            />
          </a>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="text-sm font-medium text-[#1a2332] hover:text-[#4a5568] transition-colors cursor-pointer whitespace-nowrap">
              トップページ
            </a>
            {(['core', 'pro', 'style'] as const).map((m) => (
              <a
                key={m}
                href={`/product/${m}`}
                onClick={(e) => { e.preventDefault(); navigate(`/product/${m}`); window.scrollTo(0, 0); }}
                className="text-sm font-medium text-[#1a2332] hover:text-[#4a5568] transition-colors cursor-pointer whitespace-nowrap"
              >
                {productData[m].name}
              </a>
            ))}
            <span className="text-sm font-bold text-[#1a2332] border-b-2 border-[#1a2332] whitespace-nowrap">
              製品比較
            </span>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden z-50 w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="メニュー"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl text-[#1a1a2e]"></i>
            ) : (
              <i className="ri-menu-line text-2xl text-[#1a1a2e]"></i>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden fixed inset-0 bg-white z-40 transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex flex-col pt-20 px-6 space-y-6">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); setIsMobileMenuOpen(false); }} className="text-base font-medium text-[#1a1a2e] hover:text-[#8E7CC3] transition-colors cursor-pointer py-3 border-b border-[#e8e6e3]">
              トップページ
            </a>
            {(['core', 'pro', 'style'] as const).map((m) => (
              <a
                key={m}
                href={`/product/${m}`}
                onClick={(e) => { e.preventDefault(); navigate(`/product/${m}`); window.scrollTo(0, 0); setIsMobileMenuOpen(false); }}
                className="text-base font-medium text-[#1a1a2e] hover:text-[#8E7CC3] transition-colors cursor-pointer py-3 border-b border-[#e8e6e3]"
              >
                {productData[m].name}
              </a>
            ))}
            <span className="text-base font-bold text-[#1a1a2e] py-3 border-b border-[#e8e6e3]">
              製品比較
            </span>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-[#f5f3ef] via-white to-[#f5f3ef] overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#3d6b4a]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#2c4a6e]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        <div className="relative max-w-6xl mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-[#4a4a4a] mb-10">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="hover:text-[#1a2332] transition-colors cursor-pointer">トップ</a>
            <i className="ri-arrow-right-s-line text-base"></i>
            <span className="font-medium text-[#1a2332]">製品比較</span>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#1a2332]/5 px-4 py-2 rounded-full mb-6">
              <i className="ri-scales-3-line text-[#1a2332]"></i>
              <span className="text-xs font-bold text-[#1a2332] tracking-wider">PRODUCT COMPARISON</span>
            </div>
            <h1 className="text-4xl font-bold text-[#1a2332] mb-4" style={{ fontFamily: 'Noto Serif JP, serif' }}>
              3モデル徹底比較
            </h1>
            <p className="text-base text-[#4a4a4a] max-w-2xl mx-auto leading-relaxed">
              役割・環境・目的に応じて最適なモデルをお選びください。<br />
              フロア・部署単位での混在導入も可能です。
            </p>
          </div>
        </div>
      </section>

      <ComparisonCards />
      <DiagnosisBanner />
      <ComparisonTable />
      <ComparisonCTA />

      {/* Footer */}
      <footer className="bg-[#3d6b4a] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-sm">
          <div className="mb-4 md:mb-0">
            <img 
              src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/826c552476badc913a23e7605116bff7.png" 
              alt="MeVER Chair ロゴ" 
              title="MeVER Chair"
              className="h-7 w-auto brightness-0 invert"
            />
          </div>
          <div className="flex items-center space-x-6 text-white/70">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="hover:text-white transition-colors cursor-pointer">トップページ</a>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-white transition-colors cursor-pointer">Website Builder</a>
            <span>© 2025 MeVER Chair</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
